// Centralised PostHog event names and capture helpers (ADR-028 / Phase 1m-6a).
//
// Rules:
// - NO PII in properties: use ids/counts/dates only; never name/email.
// - Dot-separated naming convention: <domain>.<action>.
// - All captures are no-ops when posthog is not initialised (dev/test without
//   key, and — since OH-72 — a guest who rejected analytics).
//
// There is deliberately NO consent check in this file. The gate is a
// before_send hook installed at init (lib/posthog.ts), because it also has to
// cover the events this module never names: $pageleave and web vitals, which
// posthog-js captures on its own. A per-helper guard here would look complete
// and leave those two running.

import { posthog } from '@/lib/posthog';

// ── Page navigation ────────────────────────────────────────────────────────────

// capturePageView is called on every router navigation (main.tsx subscribe).
// Receives the ROUTE PATTERN (e.g. /apartment/$id) — never the raw pathname
// which could contain bearer tokens or guest-identifying slugs.
export function capturePageView(routePattern: string): void {
  posthog.capture('page_view', { route: routePattern });
}

// ── Apartment ─────────────────────────────────────────────────────────────────

export function captureApartmentView(apartmentId: string): void {
  posthog.capture('apartment_view', { apartment_id: apartmentId });
}

// apartment.search fires when a guest asks an apartment page for a price on
// specific dates — the apartment-scoped counterpart of search.submitted.
//
// It used to fire on the same line as quote.requested, which made the step
// between them 100% by construction: 13 and 13 over the first two months.
// A funnel step that cannot fail measures nothing, so quote.requested now
// fires only once a quote actually comes back.
export function captureApartmentSearch(apartmentId: string, nights: number): void {
  posthog.capture('apartment.search', { apartment_id: apartmentId, nights });
}

// ── Quote ─────────────────────────────────────────────────────────────────────

// Fires when the quote RESPONSE arrives, not when it is asked for — see the
// note on captureApartmentSearch. `available` distinguishes "we quoted a
// price" from "those dates are taken", which is the difference between a
// funnel that stalls on demand and one that stalls on supply.
export function captureQuoteRequested(
  apartmentId: string,
  nights: number,
  available: boolean,
): void {
  posthog.capture('quote.requested', { apartment_id: apartmentId, nights, available });
}

// ── Hold ──────────────────────────────────────────────────────────────────────

export function captureHoldCreated(apartmentId: string): void {
  posthog.capture('hold.created', { apartment_id: apartmentId });
}

// hold.expired fires when the checkout page watches its own hold run out —
// the guest was ON the page and still did not finish. That is a different
// story from the server-side expiry the worker reports (OH-71), which covers
// the far commoner case of a tab closed long before the deadline. Keeping
// them apart is the point: one says "our checkout lost someone who was
// trying", the other says "someone wandered off".
export function captureHoldExpired(holdId: string): void {
  posthog.capture('hold.expired', { hold_id: holdId });
}

// ── Search ────────────────────────────────────────────────────────────────────

// search.submitted fires when the home form is submitted and navigates to
// /search. There was no event here at all because a stale comment in this
// module claimed the home input was "a disabled placeholder (Phase 0)" — it
// has been live for months, so the first step of the funnel was invisible.
export function captureSearchSubmitted(nights: number | null, guests: number): void {
  posthog.capture('search.submitted', { nights, guests });
}

// search.results_viewed fires once results for a search render. result_count
// is what separates "nobody searches" from "we search and show nothing" —
// the latter is a supply problem, and OH-74 exists for it.
export function captureSearchResultsViewed(resultCount: number, nights: number | null): void {
  posthog.capture('search.results_viewed', { result_count: resultCount, nights });
}

// ── Checkout ──────────────────────────────────────────────────────────────────
//
// These lived inline in routes/book.$holdId.tsx, calling posthog.capture
// directly and bypassing this module — against the rule stated at the top of
// this file. Names are unchanged so the (currently empty) history stays valid.

export function captureCheckoutStarted(holdId: string): void {
  posthog.capture('checkout.started', { hold_id: holdId });
}

export function captureCheckoutDocumentsChanged(holdId: string): void {
  posthog.capture('checkout.documents_changed', { hold_id: holdId });
}

// Which PayPal funding source the guest picked (paypal, card, …). Answers
// "do guests want to pay by card?", which is what OH-83 (Advanced Checkout)
// would be justified by — or not.
export function capturePaymentMethodSelected(holdId: string, method: string): void {
  posthog.capture('checkout.payment_method_selected', { hold_id: holdId, method });
}

export function captureCheckoutPaymentSuccess(holdId: string): void {
  posthog.capture('checkout.payment_success', { hold_id: holdId });
}

export function captureCheckoutPaymentFailed(holdId: string, reason?: string): void {
  posthog.capture('checkout.payment_failed', { hold_id: holdId, ...(reason ? { reason } : {}) });
}

// checkout.abandoned fires when the guest leaves the payment page without
// paying. This is the CLIENT-SIDE half; the dependable half is server-side (a
// hold that expires unpaid) and is deliberately a separate event, because the
// two count different things and disagreeing numbers are information, not a
// bug.
//
// `sendBeacon` is what makes this half worth having at all. Measured on
// staging: leaving via an in-app link delivered the event, while a full page
// navigation dropped it — the handler ran, but the queued batch died with the
// document. sendBeacon hands the request to the browser, which delivers it
// after the page is gone, so the common case (guest closes the tab) is the one
// that now reports rather than the one that never did.
//
// `source: 'client'` is explicit, not decorative. The server half already
// tags itself, and since OH-72 the two halves count DIFFERENT populations:
// this one fires for everyone (cookieless capture runs before a choice), the
// server one only for guests who accepted — because an unconsented hold
// carries no analytics_id. Any chart that does not split by source now moves
// with the consent rate rather than with guest behaviour.
export function captureCheckoutAbandoned(holdId: string, stage: string): void {
  posthog.capture(
    'checkout.abandoned',
    { hold_id: holdId, stage, source: 'client' },
    { transport: 'sendBeacon' },
  );
}

// ── Consent ───────────────────────────────────────────────────────────────────

// consent.choice is the only observable the consent machinery has. It MUST be
// captured while the decision is still `pending`: after a refusal is recorded,
// before_send drops everything, which is the point. In the pending state
// PostHog is cookieless — nothing is stored on the device to send it.
export function captureConsentChoice(analytics: boolean, reopened: boolean): void {
  posthog.capture('consent.choice', { analytics, reopened });
}

// ── Booking ───────────────────────────────────────────────────────────────────

// booking.viewed fires when a guest opens a confirmed booking, whether from
// the emailed link or from the cabinet — `source` tells which.
export function captureBookingViewed(source: 'token_link' | 'cabinet' | 'confirmation'): void {
  posthog.capture('booking.viewed', { source });
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export function captureMagicLinkRequested(): void {
  posthog.capture('magic_link.requested');
}

export function captureMagicLinkConsumed(): void {
  posthog.capture('magic_link.consumed');
}

// sign_in_code.used fires when a guest signs in by typing the code from the
// email instead of following the link (OH-149). Kept as its own event rather
// than a property on magic_link.consumed, which is captured by the cabinet on
// first load and cannot tell which carrier brought the session. The count is
// the answer to "does the installed-app path get used" — the question that
// motivated the code in the first place.
export function captureSignInCodeUsed(): void {
  posthog.capture('sign_in_code.used');
}
